import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TableRow,
  Typography,
} from "@material-ui/core";

import useTabPanelStyles from "../../styles/TabPanel.style";
import useStyles from "./WithdrawRequestsTabPanel.style";
import TableData, {
  StyledTableCell,
  StyledTableRow,
} from "../../components/TableData";
import Paginator from "../../../../models/Paginator";
import moment from "moment";
import { ActionButton } from "../../components/Buttons";
import { InputField } from "../../../../components/input-field/InputField";
import CurrencyFormat from "react-currency-format";
import WithdrawRequest from "../../../../models/WithdrawRequest";
import { displayWalletType } from "../../../../settings/WalletType";
import { Tag } from "antd";
import ICIPAdminService from "../../../../services/ICIPAdminService";
import { AiOutlineCheckCircle, AiOutlineSearch } from "react-icons/ai";

interface WithdrawRequestsTabPanelProps {
  paginator: Paginator;
  withdrawRequests: any;
}

interface WithdrawRequestValidationAttr {
  id: number;
}

export const WithdrawRequestsTabPanel = (props: WithdrawRequestsTabPanelProps) => {
  const classes = useStyles(props);
  const tabPanelClasses = useTabPanelStyles(props);
  const intl = useIntl();
  // Props
  const { paginator } = props;
  // State
  const [withdrawRequests, setWithdrawRequests] = useState(paginator.items);
  const [activating, setActivating] = useState(
    (): Array<WithdrawRequestValidationAttr> => []
  );

  // Callback

  const activatingItemIndex = (key: number): number => {
    return activating.findIndex((item) => item.id === key);
  };

  const updateActivatingItems = (key: number, remove: boolean = false) => {
    let index = activatingItemIndex(key);
    let data = activating;

    if (remove) {
      if (index >= 0) {
        data.splice(index, 1);

        setActivating([...data]);
      }
    } else {
      if (index < 0) {
        data.push({ id: key });

        setActivating([...data]);
      }
    }
  };

  const withdrawRequestValidation = (withdrawRequestId: number) => {
    updateActivatingItems(withdrawRequestId);
    const response = ICIPAdminService.withdrawRequestValidation(withdrawRequestId);

    response.then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let withdrawRequest = WithdrawRequest.fromJson(
          JSON.parse(JSON.stringify(response.data.data))
        );
        handleUpdateWithdrawRequest(withdrawRequest);
      } else {
        
      }

      updateActivatingItems(withdrawRequestId, true);
    });
  };

  const handleUpdateWithdrawRequest = (withdrawRequest: WithdrawRequest) => {
    let oldWithdrawRequests = withdrawRequests;

    let index = withdrawRequests
      .map(function (item) {
        return item.id;
      })
      .indexOf(withdrawRequest.id);

    oldWithdrawRequests[index] = withdrawRequest;

    // Update list
    setWithdrawRequests([...oldWithdrawRequests]);
  };

  const searchFilter = (query: string) => {
    if (query !== "") {
      const results = withdrawRequests.filter((withdrawRequest: WithdrawRequest) => {
        let searchCompanyName = withdrawRequest.company_name
          .toLowerCase()
          .startsWith(query.toLowerCase());

        return searchCompanyName;
      });
      setWithdrawRequests(results);
    } else {
      setWithdrawRequests(paginator.items);
    }
  };

  // Renders

  const render = () => {
    return (
      <Box component="div">
        <TableData
          paginator={
            paginator.total !== 0
              ? {
                  total: paginator.total,
                  page: paginator.page,
                  totalPage: paginator.total_page,
                  title: renderWithdrawRequestsSearch(),
                  handlePrevAction:
                    paginator.prev_page !== null ? () => {} : null,
                  handleNextAction:
                    paginator.next_page !== null ? () => {} : null,
                }
              : null
          }
          rows={renderWithdrawRequestsTableRows()}
          header={renderWithdrawRequestsTableHeader()}
        />
      </Box>
    );
  };

  const renderWithdrawRequestsTableRows = () => {
    return withdrawRequests.map((row, index) => {
      let withdrawRequest = row as WithdrawRequest;

      return (
        <StyledTableRow key={index}>
          <StyledTableCell component="td" scope="row">
            {`${withdrawRequest.company_name} `}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row"  className={classes.blueCell}>
            <FormattedMessage
                id={displayWalletType(withdrawRequest.wallet_type)}
              />
          </StyledTableCell>
          <StyledTableCell
            component="td"
            scope="row"
          >
            <CurrencyFormat
                  value={withdrawRequest.amount}
                  prefix={withdrawRequest.amount_currency}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
              />
                
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <CurrencyFormat
                    value={withdrawRequest.icip_commission_amount}
                    prefix={withdrawRequest.icip_commission_currency}
                    displayType="text"
                    thousandSeparator={true}
                    fixedDecimalScale={true}
                    decimalScale={2}
                />
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {moment(withdrawRequest.created_at).format("ddd, MMM yyyy @hh:mm")}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {
            withdrawRequest.validation_date !== null ?
              <Tag className={classes.validated}>
                  {intl.formatMessage({ id: "sharedScreen.valided" })}
              </Tag>
              :
              <Tag className={classes.unValidated}>
                  <FormattedMessage id="sharedScreen.unValided" />
              </Tag>
            }
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            { withdrawRequest.validation_date === null ? ( 
              <> 
                {activatingItemIndex(withdrawRequest.id) < 0 ? (
                  <ActionButton
                    ariaLabel="Validate withdraw"
                    customColor="#26a69a"
                    title={intl.formatMessage({ id: "sharedScreen.validateWithdrawRequest" })}
                    onClick={(_) => withdrawRequestValidation(withdrawRequest.id)}
                  >
                    <AiOutlineCheckCircle />
                  </ActionButton>
                ) : (
                  <Box component="span" mr={2} display="inline-block">
                    <CircularProgress
                      color="secondary"
                      disableShrink={false}
                      size={14}
                      thickness={4}
                    />
                  </Box>
                )}
              </>
            ) : (
              <></>
            )}
          </StyledTableCell>
        </StyledTableRow>
      );
    });
  };

  const renderWithdrawRequestsTableHeader = () => {
    return (
      <TableRow>
        <StyledTableCell>
          <FormattedMessage id="sharedScreen.businessName" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.wallet" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.amount" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.cost" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.date" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.status" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.actions" />
        </StyledTableCell>
      </TableRow>
    );
  };

  const renderWithdrawRequestsSearch = () => {
    return (
      <Box width="40%" marginBottom={3}>
        <InputField
          id="search"
          name="search"
          fullWidth
          placeholder={intl.formatMessage({
            id: "sharedScreen.typeToSearch",
          })}
          type="text"
          endAdornment={
            <InputAdornment position="end">
              <AiOutlineSearch />
            </InputAdornment>
          }
          onChange={(event) => searchFilter(event.target.value)}
        />
      </Box>
    );
  };

  useEffect(() => {
    if (paginator.items.length !== withdrawRequests.length) {
      setWithdrawRequests([...paginator.items]);
    }

    // eslint-disable-next-line
  }, [paginator.items]);

  return (
    <>
      <Box component="div" className={tabPanelClasses.root}>
        <Box component="div" className={classes.tabsPanel}>
          <Box component="div" className={classes.wrapper}>
            <Box component="div" className={classes.leftHeader}>
              <Typography variant="h3" component="h3">
                <FormattedMessage id="sharedScreen.businessWithdrawRequestList" />
              </Typography>

              <Box component="div" className={classes.trailing}>
                <Typography variant="h3" component="h3">
                  {`# ${paginator.total}`}
                </Typography>
              </Box>
            </Box>
            <Box component="div" className={classes.leftWrapper}>
              {render()}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};